import type { UseSelectState, UseSelectStateChangeOptions, UseSelectStateChange } from 'downshift';
import { useSelect } from 'downshift';
import * as React from 'react';

export interface SelectionValue {
    id: string;
}

interface UseMultiselectProps<TValue extends SelectionValue> {
    items: TValue[];
    itemToString: (item: TValue | null) => string;
    selectedItems: TValue[];
    onSelectionChanged: (items: TValue[]) => void;
    initialIsOpen?: boolean;
}

export const useMultiselect = <TValue extends { id: string }>(props: UseMultiselectProps<TValue>) => {
    const { items, itemToString, selectedItems, onSelectionChanged, initialIsOpen } = props;

    const selectionReducer = (state: UseSelectState<TValue>, actionAndChanges: UseSelectStateChangeOptions<TValue>) => {
        const { changes, type } = actionAndChanges;

        switch (type) {
            case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
            case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
            case useSelect.stateChangeTypes.ItemClick:
                return {
                    ...changes,
                    isOpen: true, // keep the menu open after selection.
                    highlightedIndex: state.highlightedIndex, // preserve last highlighted item after selection.
                };
            case useSelect.stateChangeTypes.ToggleButtonBlur:
                return {
                    ...changes,
                    selectedItem: null, // prevent highlighted item toggling on blur (i.e. on tabbing)
                };
            default:
                return changes;
        }
    };

    const toggleButtonRef = React.useRef<HTMLButtonElement>(null);

    const isSelected = (value: TValue) => selectedItems.map(item => item.id).includes(value.id);

    const onSelectionStateChange = (stateChange: UseSelectStateChange<TValue>) => {
        const { selectedItem } = stateChange;

        if (!selectedItem) {
            return;
        }

        const newItems = isSelected(selectedItem)
            ? selectedItems.filter(item => item.id !== selectedItem.id)
            : [...selectedItems, selectedItem];

        onSelectionChanged(newItems);
    };

    const { isOpen, highlightedIndex, getLabelProps, getToggleButtonProps, getMenuProps, getItemProps } = useSelect({
        selectedItem: null,
        items,
        itemToString,
        stateReducer: selectionReducer,
        onStateChange: onSelectionStateChange,
        initialIsOpen,
    });

    return {
        isOpen,
        isSelected,
        highlightedIndex,
        getLabelProps: () => getLabelProps(),
        getToggleButtonProps: () => getToggleButtonProps({ ref: toggleButtonRef }),
        getMenuProps: () => getMenuProps({ 'aria-multiselectable': true, tabIndex: -1 }),
        getItemProps: (item: TValue) =>
            getItemProps({
                item,
                'aria-selected': isSelected(item),
                tabIndex: -1,
                // Focus the toggle button on item click in case there is some kind of list wrapper
                // that allows hijacking the focus yet keeps the list open.
                onMouseDown: () => {
                    toggleButtonRef.current?.focus();
                },
            }),
    };
};
